import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { buildFilterKey } from '@/services/filterEngine'
import { getResponseColor } from '@/utils/colors'

// Q8: BEFORE question - baseline support for parks before exposure to information
const SUPPORT_QUESTIONS = [
  { id: 'Support_NewParks', label: 'Please say how much you would SUPPORT or OPPOSE the creation of more parks and conservation areas to protect nature, land and freshwater in your province.' },
] as const

// Response order: Strongly support → Support → Neutral/Unsure → Oppose → Strongly oppose
// Includes variants for sorting to handle different label formats
const SUPPORT_RESPONSE_ORDER = ['Strongly support', 'Support', 'Undecided/Neutral', 'Neutral / Unsure', 'Neutral/Unsure', 'Neutral', 'Unsure', 'Oppose', 'Strongly oppose']

// Legend labels (clean display)
const LEGEND_LABELS = ['Strongly support', 'Support', 'Neutral / Unsure', 'Oppose', 'Strongly oppose']

// Sort responses according to support order
const sortSupportResponses = (responses: Array<{ label: string; percentage: number }>) => {
  return [...responses].sort((a, b) => {
    const aIdx = SUPPORT_RESPONSE_ORDER.indexOf(a.label)
    const bIdx = SUPPORT_RESPONSE_ORDER.indexOf(b.label)
    if (aIdx === -1) return 1
    if (bIdx === -1) return -1
    return aIdx - bIdx
  })
}

export function SupportMoreParks() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          Support for More Parks <span className="text-base font-normal italic text-slate-500">(first of two)</span>
        </h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">Break: {breakName}</span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b border-slate-100 dark:border-slate-700">
          {LEGEND_LABELS.map(label => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: getResponseColor('support', label) }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
            </div>
          ))}
        </div>

        <div className="space-y-6">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading...</p>
          ) : (
            SUPPORT_QUESTIONS.map(({ id, label }) => {
              const question = data.questions[id]
              if (!question) return null

              const rows = breakValues.map(value => {
                const filterKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined, selectedProvince)
                let questionData = question.data[filterKey]
                if (!questionData && selectedProvince) {
                  questionData = question.data[buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)]
                }
                return { value, filterKey, data: questionData }
              }).filter(row => row.data)

              if (rows.length === 0) return null

              return (
                <div key={id} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0 last:pb-0">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{label}</h3>
                  <div className="space-y-2">
                    {rows.map(row => (
                      <div key={row.filterKey} className="flex items-center gap-2">
                        <div className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0" title={isBreakActive ? getDisplayName(row.value) : 'National Sample'}>
                          {isBreakActive ? getDisplayName(row.value) : 'National Sample'}
                        </div>
                        <div className="flex-1">
                          <HorizontalStackedBar
                            segments={sortSupportResponses(row.data!.responses)}
                            scaleType="support"
                            height={isBreakActive ? 24 : 28}
                            showLabels={false}
                            normalize={true}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
